import React from 'react';
import { decodeHtmlEntities } from '../functions/htmlEncoders';

/**
 * Read-only Claude document shown beside the Gemini review page.
 *
 * Side-by-side compare is only useful if both columns look like the same
 * document. This panel therefore uses the same white-page chrome, left-
 * aligned type, section heading, paragraph heading, and body card classes
 * as the Gemini column on RD_CI / SSI / DD / PAD / Semble. It is not
 * interactive: approvals and edits stay on the Gemini draft.
 *
 * Paragraphs with no Gemini content are omitted so the two columns stay
 * aligned with what the review page actually shows. A missing Claude
 * draft (partial generate) is called out rather than left blank.
 *
 * @param {Object} props
 * @param {Object} props.docStructure - Same section map the review page uses
 * @param {Object|null} props.claudeOutputs - { paragraphCode: text }
 * @param {Object} props.reportOutputs - Stored Gemini outputs, used to
 *   decide which paragraphs belong on the page
 * @param {Object} [props.sectionSubtitles] - Optional SSI-style subtitle per section
 */
export function CompareClaudePanel({
  docStructure,
  claudeOutputs,
  reportOutputs,
  sectionSubtitles,
}) {
  return (
    <div
      className="w-1/2 relative m-4 p-16 border shadow-md bg-white overflow-y-auto"
      aria-label="Claude draft"
    >
      <div className="space-y-8 text-left">
        {Object.entries(docStructure || {}).map(([title, paragraphs]) => {
          const items = Object.entries(paragraphs || {}).filter(
            ([, { code }]) => reportOutputs?.[code] !== undefined && reportOutputs?.[code] !== ''
          );
          if (items.length === 0) return null;

          const subtitle = sectionSubtitles?.[title];

          return (
            <div key={title} className="mb-8">
              <h2
                className={`text-2xl font-bold text-blue-500 ${
                  subtitle ? 'mb-1' : 'mb-4'
                }`}
              >
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm text-gray-400 italic mb-4">{subtitle}</p>
              )}

              {items.map(([key, { title: subTitle, code }]) => {
                const raw = claudeOutputs?.[code];
                const hasDraft = raw !== undefined && raw !== '';
                return (
                  <div key={key} className="mb-4">
                    {subTitle && (
                      <h3 className="font-semibold mb-2 text-gray-900">
                        {subTitle}
                      </h3>
                    )}
                    <div className="relative text-sm font-medium p-2 rounded-lg bg-violet-100">
                      {hasDraft ? (
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {decodeHtmlEntities(raw)}
                        </p>
                      ) : (
                        <p className="text-gray-500 italic leading-relaxed">
                          No Claude draft for this paragraph.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />
    </div>
  );
}
